import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function KnowsleyCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Knowsley Pothole Claims | 69% of U-Roads Amber or Red | Fixtyer"
        description="Knowsley Council earns GREEN on spend but AMBER overall — with 55% of unclassified roads now amber (up from 44% in 2020), 14% in RED, and the council admitting roads previously green are sliding towards failure. See what the DfT rating means for your claim."
        canonical="/councils/knowsley"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Knowsley', url: '/councils/knowsley' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Knowsley earns a GREEN spend scorecard — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but the ratings that matter for your claim are road condition (AMBER) and best practice (also AMBER). Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. Knowsley\'s own data shows 69% of unclassified roads in amber or red condition, and the council admits amber roads were previously green and will deteriorate further without investment.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 436km — 74% of Knowsley\'s 591km carriageway network. At the latest GAIST survey, 14.13% were in RED condition and 55.21% in amber — meaning roughly 302km of residential streets need maintenance now or soon. Drainage on unclassified roads is cleansed biennially, not annually, per the council\'s own inspection schedule.',
          },
          {
            question: 'Does Knowsley admitting amber roads were previously green help my claim?',
            answer: 'Yes. Knowsley states that while RED percentages have stayed relatively consistent, "there has been a significant increase in the percentage of roads that are now categorised as amber that were previously green" and that "without significant investment the roads which are currently amber will deteriorate into the red category." That is documented knowledge of a deteriorating pipeline — not a stable network.',
          },
          {
            question: 'Pothole fills rose to 2,177 in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Knowsley filled 9,035 carriageway potholes over five years while U-road green condition fell from 42.10% to 30.65%. The council itself notes that "while filling individual potholes provides short-term relief, resurfacing addresses the root cause of road deterioration." Its 2025/26 pothole-funding resurfacing programme covers just 3km of named roads.',
          },
          {
            question: 'Why is Knowsley AMBER on best practice as well as condition?',
            answer: 'Unlike councils that earn GREEN across spend and best practice, Knowsley is AMBER on both condition and best practice while GREEN on spend. The council has asset management plans, NHT surveys and a new Tarmac maintenance contract — but PAS2161 accreditation does not take effect until 2026/27, and drainage on U-roads is only cleansed every two years. Expect a structured Section 58 defence, but with identifiable process gaps on residential roads.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Knowsley Council"
        councilSlug="knowsley"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/knowsley"
        headline="Knowsley Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Knowsley</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Knowsley: 69% of U-Roads Now Amber or RED
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Knowsley Council projects £10.4m on highway maintenance in 2025/26 and earns a{' '}
            <strong className="text-emerald-400">GREEN</strong> spend scorecard. Yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> — because{' '}
            <strong className="text-rose-400">69% of unclassified roads are now amber or red</strong>, green
            condition on residential streets has fallen from 42% to 31%, and the council admits roads
            previously green are sliding towards failure.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">55.21%</div>
            <div className="text-white font-medium mt-2">U-roads in amber condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 43.70% in 2020 — roughly 241km of Knowsley&apos;s 436km unclassified network.
              The council warns these roads will become RED without significant investment.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of GAIST condition survey data from Knowsley&apos;s own transparency report —
            RED stable, amber rising, green falling
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (79km — 13% of network): mostly amber, not green</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4.29%</td>
                    <td className="px-4 py-2 text-center">61.08%</td>
                    <td className="px-4 py-2 text-center">34.63%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">4.51%</td>
                    <td className="px-4 py-2 text-center">61.34%</td>
                    <td className="px-4 py-2 text-center">34.15%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">4.33%</td>
                    <td className="px-4 py-2 text-center">62.60%</td>
                    <td className="px-4 py-2 text-center">33.03%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">3.52%</td>
                    <td className="px-4 py-2 text-center">54.39%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">41.70%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">3.61%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">59.70%</td>
                    <td className="px-4 py-2 text-center font-bold">36.68%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED condition is low, but roughly three-fifths of A-road carriageway is amber —
              maintenance may be required soon. The 2023 green spike reversed in 2024.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (65km — 11% of network): amber rising, green falling</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">5.70%</td>
                    <td className="px-4 py-2 text-center">48.38%</td>
                    <td className="px-4 py-2 text-center">45.92%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">6.34%</td>
                    <td className="px-4 py-2 text-center">51.49%</td>
                    <td className="px-4 py-2 text-center">42.16%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5.61%</td>
                    <td className="px-4 py-2 text-center">55.03%</td>
                    <td className="px-4 py-2 text-center">38.94%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5.38%</td>
                    <td className="px-4 py-2 text-center">54.73%</td>
                    <td className="px-4 py-2 text-center">37.64%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.84%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">56.37%</td>
                    <td className="px-4 py-2 text-center font-bold">37.79%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED B/C roads are broadly stable at around 6%, but amber has risen from 48% to 56% while
              green has fallen from 46% to 38%. Over 62% of classified local roads now need maintenance
              now or soon.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£7.26m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£10.4m</div>
                <div className="text-brand-300 text-sm">Total projected spend (capital + revenue)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">70.10%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share 2025/26</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Knowsley projects spending{' '}
              <span className="text-emerald-400">£10,361,708</span> on highway maintenance in 2025/26 —
              including £1,010,708 in additional DfT pothole funding — yet the council&apos;s own condition
              data shows a deteriorating amber pipeline across three-quarters of its network.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road majority */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 436km Residential Majority
          </h2>
          <p className="text-surface-600 mb-8">
            74% of Knowsley&apos;s carriageway network is unclassified roads — surveyed annually by GAIST
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-rose-700">Red</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-amber-700">Amber</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-emerald-700">Green</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14.20%</td>
                  <td className="px-6 py-4 text-center">43.70%</td>
                  <td className="px-6 py-4 text-center">42.10%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14.62%</td>
                  <td className="px-6 py-4 text-center">46.97%</td>
                  <td className="px-6 py-4 text-center">38.41%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14.80%</td>
                  <td className="px-6 py-4 text-center">47.68%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">28.64%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14.92%</td>
                  <td className="px-6 py-4 text-center">50.24%</td>
                  <td className="px-6 py-4 text-center">32.02%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14.13%</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">55.21%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">30.65%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Amber Pipeline</h3>
              <p className="text-surface-600 text-sm mb-3">
                RED U-roads have held steady at roughly 14% for five years — about 62km of residential
                streets in the worst category. But amber has climbed from 43.70% to 55.21%, while green
                has collapsed from 42.10% to 30.65%.
              </p>
              <p className="text-surface-600 text-sm">
                Combined, <strong>69% of Knowsley&apos;s unclassified network</strong> is now amber or red —
                approximately 302km where maintenance is needed now or imminent.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Biennial Drainage on U-Roads</h3>
              <p className="text-amber-800 text-sm mb-3">
                Knowsley&apos;s Annex B inspection schedule shows drainage silt surveys on classified roads
                are annual, but on unclassified roads they are{' '}
                <strong>biennially</strong> — every two years.
              </p>
              <p className="text-amber-800 text-sm">
                Blocked drainage accelerates carriageway failure. On the road type that makes up three-quarters
                of the borough, the council checks drains half as often as on A, B and C roads.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for
              knowing the condition of its roads. For Knowsley&apos;s unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• If 55% of U-roads are amber, what preventative work was done on yours before the pothole formed?</li>
              <li>• When were the drains on your street last cleansed — annually or on the biennial U-road cycle?</li>
              <li>• The council admits amber roads were previously green — was your road in that sliding category?</li>
              <li>• Priority 1 emergency defects rose from 586 to 756 in three years — was your defect visible earlier?</li>
            </ul>
            <p className="text-white font-medium">
              A council that documents a deteriorating pipeline cannot claim surprise when individual
              defects cause damage.
            </p>
          </div>
        </div>
      </section>

      {/* Potholes and defects */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            9,035 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Carriageway pothole fills and safety defect repairs from Knowsley&apos;s own report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Carriageway potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Total safety defects repaired</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">1,398</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not reported</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,891</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not reported</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,672</td>
                  <td className="px-6 py-4 text-center font-bold">5,740</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">1,897</td>
                  <td className="px-6 py-4 text-center font-bold">6,043</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,177</td>
                  <td className="px-6 py-4 text-center font-bold">5,260</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">9,035</td>
                  <td className="px-6 py-4 text-center font-bold">17,043*</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 40mm Threshold</h3>
              <p className="text-surface-600 text-sm">
                Knowsley defines a pothole as 40mm deep or more on carriageways and 25mm on footways.
                Defects below that threshold may not be recorded or repaired under the council&apos;s policy —
                yet can still damage tyres, wheels and suspension.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Emergency Defects Rising</h3>
              <p className="text-amber-800 text-sm">
                Priority 1 defects — requiring repair within two hours — rose from 586 in 2022/23 to
                756 in 2024/25, a 29% increase. Priority 3 defects (14-day repair window) more than
                tripled from 72 to 254 over the same period.
              </p>
            </div>
          </div>

          <p className="text-surface-500 text-xs mb-0">
            *Total safety defects sum 2022/23–2024/25 only; earlier years not published in the report.
          </p>
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Council&apos;s Own Condition Admission
          </h2>
          <p className="text-surface-600 mb-8">
            Knowsley&apos;s transparency report acknowledges a deteriorating pipeline — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The Council manages the condition of the highway network effectively using the resources available, whilst the percentage of the carriageway being in the red category has remained relatively consistent over the last five years there has been a significant increase in the percentage of roads that are now categorised as amber that were previously green. Without significant investment the roads which are currently amber will deteriorate into the red category and become expensive to repair."
              source="Knowsley Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="While filling individual potholes provides short-term relief, resurfacing addresses the root cause of road deterioration, ensuring a more permanent solution and extend the life of the road."
              source="Knowsley Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Admission Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Knowsley formally documents that green roads are becoming amber, and amber roads
                will become red without investment. That is not a network in equilibrium — it is
                documented decline with a known trajectory.
              </p>
              <p className="text-surface-600 text-sm">
                The council&apos;s 2025/26 pothole-funding resurfacing programme covers just 3km across
                named estate roads — a fraction of the 302km currently amber or red on U-roads alone.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2025/26 Resurfacing Plans</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Higher Road, Lower Road, Kirkby Row, St Helens Road — 1.5km combined</li>
                <li>• Milton Avenue — 0.8km</li>
                <li>• Evans Street estate roads — 0.7km across six streets</li>
                <li>• Total pothole-funding resurfacing: 3km on a 591km network</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Spending detail */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years of Spending
          </h2>
          <p className="text-surface-600 mb-8">
            Capital and revenue maintenance figures from Knowsley&apos;s transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative %</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Reactive %</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">£7,263,708</td>
                  <td className="px-4 py-3 text-center">£3,098,000</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">70.10%</td>
                  <td className="px-4 py-3 text-center">29.90%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£4,484,509</td>
                  <td className="px-4 py-3 text-center">£3,215,000</td>
                  <td className="px-4 py-3 text-center">58.24%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">41.76%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£8,722,000</td>
                  <td className="px-4 py-3 text-center">£2,765,000</td>
                  <td className="px-4 py-3 text-center">75.93%</td>
                  <td className="px-4 py-3 text-center">24.07%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£5,176,000</td>
                  <td className="px-4 py-3 text-center">£2,765,000</td>
                  <td className="px-4 py-3 text-center">65.18%</td>
                  <td className="px-4 py-3 text-center">34.82%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£2,587,250</td>
                  <td className="px-4 py-3 text-center">£3,415,000</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">43.10%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">56.90%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£3,473,573</td>
                  <td className="px-4 py-3 text-center">£2,882,000</td>
                  <td className="px-4 py-3 text-center">54.65%</td>
                  <td className="px-4 py-3 text-center">45.35%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">£1.76 Billion Asset, 0.59% Maintenance</h3>
            <p className="text-brand-200 mb-4">
              Knowsley values its highway assets at £1,756,151,895 under HAMFIG/CIPFA methodology.
              Projected 2025/26 maintenance spend represents just 0.59% of that asset value — while
              reactive repairs consumed 41.76% of total spend as recently as 2024/25.
            </p>
            <p className="text-white font-medium">
              GREEN spend does not mean GREEN roads. It means the council is deploying its budget —
              but on a network where the council itself warns amber roads are sliding towards red.
            </p>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Knowsley invests seriously — here&apos;s how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council&apos;s Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — £10.4m projected total maintenance in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual GAIST condition surveys across the full adopted network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented Highway Asset Management Strategy and Performance Framework
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> New 7-year Tarmac maintenance contract with KPIs from February 2025
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> NHT customer survey participation since 2012
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on classified roads with annual survey data.
                Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 69% of U-roads amber or red, green down from 42% to 31%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits amber roads were previously green and will become red
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Biennial drainage cleansing on U-roads vs annual on classified roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 9,035 potholes filled in five years — reactive repairs rising to 2,177 in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Just 3km of pothole-funding resurfacing planned on a 591km network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — PAS2161 transition not until 2026/27
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and documented asset management, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, cite the 55% amber rate and biennial drainage schedule</li>
              <li>• Whether the defect exceeded Knowsley&apos;s 40mm pothole threshold — councils sometimes dismiss sub-threshold damage</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Knowsley&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Knowsley?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No amber-pipeline argument</li>
                <li>• No prior-report search</li>
                <li>• No biennial drainage schedule cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 69% U-road amber/red rate documented</li>
                <li>✅ Council&apos;s green-to-amber admission quoted</li>
                <li>✅ 9,035 potholes in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Knowsley</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=knowsley"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Knowsley Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Knowsley earns a GREEN spend scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but the ratings that matter for your claim are road
                condition (AMBER) and best practice (also AMBER). Section 58 turns on whether the specific
                defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend.
                Knowsley&apos;s own data shows 69% of unclassified roads in amber or red condition, and the council
                admits amber roads were previously green and will deteriorate further without investment.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 436km — 74% of Knowsley&apos;s 591km carriageway network. At the latest GAIST
                survey, 14.13% were in RED condition and 55.21% in amber — meaning roughly 302km of
                residential streets need maintenance now or soon. Drainage on unclassified roads is cleansed
                biennially, not annually, per the council&apos;s own inspection schedule.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Knowsley admitting amber roads were previously green help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Knowsley states that while RED percentages have stayed relatively consistent, &ldquo;there
                has been a significant increase in the percentage of roads that are now categorised as amber
                that were previously green&rdquo; and that &ldquo;without significant investment the roads which are
                currently amber will deteriorate into the red category.&rdquo; That is documented knowledge of a
                deteriorating pipeline — not a stable network.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole fills rose to 2,177 in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Knowsley filled 9,035 carriageway potholes over five years while U-road green condition
                fell from 42.10% to 30.65%. The council itself notes that &ldquo;while filling individual potholes
                provides short-term relief, resurfacing addresses the root cause of road deterioration.&rdquo; Its
                2025/26 pothole-funding resurfacing programme covers just 3km of named roads.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is Knowsley AMBER on best practice as well as condition?
              </h3>
              <p className="text-surface-600 text-sm">
                Unlike councils that earn GREEN across spend and best practice, Knowsley is AMBER on both
                condition and best practice while GREEN on spend. The council has asset management plans, NHT
                surveys and a new Tarmac maintenance contract — but PAS2161 accreditation does not take effect
                until 2026/27, and drainage on U-roads is only cleansed every two years. Expect a structured
                Section 58 defence, but with identifiable process gaps on residential roads.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.knowsley.gov.uk/streets-roads-and-transport/roads/highways/annual-progress-report-additional-highways-maintenance-0"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Knowsley Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
